import useAuthStore from "@/stores/user/authStore";
import { Coordinates, ItineraryItem, Trip } from "@/types/trip";
import { ChatHistory, ChatHistoryResponse } from "@/types/chat";
import { supabase } from "@/config/supabaseClient";
import useToraStore, {
  AccommodationResult,
} from "@/stores/tripBuilder/useToraStore";

export interface TripContext {
  trip: {
    trip_id: string;
    itinerary: {
      date: string;
      items: { id: string; name: string; start_time: string }[];
    }[];
  };
  location: {
    id: string;
    name: string;
    country: string;
    coordinates: Coordinates;
    timezone: string;
  };

  transport: {
    from_location: string;
    current_location: string;
    to_location: string;
  };
  date: {
    start_date: string;
    end_date: string;
  };
  waytora_id: string;
  viator_id: string;
}

export interface ChatMessage {
  text: string;
  isUser: boolean;
  timestamp?: Date;
  status?: "sending" | "sent" | "error";
}

interface Endpoints {
  trip: string;
  chat: string;
  description: string;
  activityDescription: string;
}

interface DescriptionBody {
  trip: Trip;
}

interface ActivityDescriptionBody {
  locationName: string;
  activityName: string;
}

interface ChatBody {
  chat_id: string;
  trip_context: TripContext;
  new_message: string;
  user_name: string;
}

const BASE_URLS = {
  development: "http://localhost:8005/ai",
  production: "https://api.waytora.com/ai",
} as const;

export class AIService {
  private static BASE_URL =
    import.meta.env.VITE_NODE_ENV === "development"
      ? BASE_URLS.development
      : BASE_URLS.production;

  private static ENDPOINTS: Endpoints = {
    trip: "trip",
    chat: "trip/chat",
    description: "trip/description",
    activityDescription: "trip/activity-description",
  };

  static async getAccessToken(): Promise<string> {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token ?? "";
  }

  private static handleSuggestion(
    id?: string,
    name?: string,
    description?: string,
    types?: string[],
    image?: { url: string; isMain: boolean; source: string }
  ) {
    const { addSuggestion } = useToraStore.getState();

    // Only add if we have the required fields
    if (id && name && description && image) {
      // Add to both legacy suggestions and typed experiences
      const suggestion = {
        id,
        name,
        description,
        types: types || [],
        image,
      };

      addSuggestion(suggestion);
    }
  }

  private static handleAccommodation(chunk: AccommodationResult) {
    const { addAccommodation } = useToraStore.getState();

    if (chunk) {
      addAccommodation({
        id: chunk.hotelId.toString(),
        itemType: "accommodation",
        name: chunk.hotelName,
        roomType: chunk.roomtypeName || "",
        starRating: chunk.starRating,
        reviewScore: chunk.reviewScore,
        reviewCount: chunk.reviewCount,
        currency: chunk.currency,
        dailyRate: chunk.dailyRate,
        image: {
          url: chunk.imageURL,
          isMain: true,
          source: "hotel",
        },
        includeBreakfast: chunk.includeBreakfast,
        freeWifi: chunk.freeWifi,
        coordinates: {
          latitude: chunk.latitude,
          longitude: chunk.longitude,
        },
        policies: chunk.policies,
        address: chunk.addressline1,
      });
    }
  }

  private static handleChunk(chunk: any, type: "suggest" | "accommodation") {
    const { setShowRecommendations, setShowAccommodations, clearAll } =
      useToraStore.getState();
    clearAll();
    if (type === "suggest") {
      setShowRecommendations(true);
      const results = chunk.results;
      if (results) {
        results.forEach((result: any) => {
          this.handleSuggestion(
            result.id,
            result.name,
            result.description,
            result.types,
            result.image
          );
        });
      }
    } else if (type === "accommodation") {
      setShowAccommodations(true);
      const results = chunk.results;
      if (results) {
        results.forEach((result: AccommodationResult) => {
          this.handleAccommodation(result);
        });
      }
    }
  }

  static async streamResponse(
    endpoint: keyof Endpoints,
    body: ChatBody | DescriptionBody | ActivityDescriptionBody,
    onChunk: (chunk: string) => void,
    onError?: (error: Error) => void
  ): Promise<void> {
    try {
      const accessToken = await this.getAccessToken();
      const response = await fetch(
        `${this.BASE_URL}/${this.ENDPOINTS[endpoint]}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const error = new Error(
          errorData?.message || `HTTP error! status: ${response.status}`
        );
        throw error;
      }

      if (!response.body) {
        throw new Error("Response body is null");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      while (reader) {
        const { done, value } = await reader.read();

        if (done) break;

        try {
          const chunk = decoder.decode(value);

          onChunk(chunk);
        } catch (decodeError) {
          console.error("Error decoding chunk:", decodeError);
          onError?.(decodeError as Error);
        }
      }
    } catch (error) {
      console.error("Stream error:", error);
      onError?.(error as Error);
      throw error;
    } finally {
      // Cleanup if needed
    }
  }

  // Helper methods for specific endpoints
  static async streamChat(
    chatId: string,
    streamId: string,
    onChunk: (data: any) => void,
    onError?: (error: Error) => void
  ): Promise<void> {
    try {
      const accessToken = await this.getAccessToken();
      const response = await fetch(
        `${this.BASE_URL}/trip/chat/stream?stream_id=${streamId}&chat_id=${chatId}`,
        {
          headers: {
            Accept: "text/event-stream",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("No reader available");
      }

      let buffer = "";
      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Split the buffer into lines
        const lines = buffer.split("\n");

        // Process all complete lines
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              // Remove the "data: " prefix and parse the JSON
              const jsonStr = line.slice(6);
              const parsed = JSON.parse(jsonStr);

              // Handle different message types
              if (parsed.type === "content" && parsed.data?.content) {
                // Text content
                onChunk({
                  status: "in-progress",
                  data: {
                    type: "text",
                    text: parsed.data.content,
                  },
                });
              } else if (
                parsed.type === "status" ||
                parsed.type === "complete"
              ) {
                // Status updates
                onChunk({
                  status: "in-progress",
                  data: {
                    type: "status",
                    status: parsed.data?.status || "in_progress",
                  },
                });
              } else if (
                parsed.type === "fn_start" ||
                parsed.type === "flight_search_processing"
              ) {
                onChunk({
                  status: "in-progress",
                  data: {
                    type: parsed.type,
                    text: parsed.data?.awaiting_response,
                  },
                });
              } else if (
                parsed.type === "actionable_item" ||
                parsed.type === "flight_search_results"
              ) {
                // Pass through other types
                onChunk({
                  status: "in-progress",
                  data: {
                    type: parsed.type,
                    data: parsed.data,
                  },
                });
              } else if (parsed.type === "place_details_results") {
                this.handleChunk(parsed.data, "suggest");
              } else if (parsed.type === "accommodation_results") {
                this.handleChunk(parsed.data, "accommodation");
              }
            } catch (e) {
              // Ignore parsing errors for incomplete chunks
              if (!done) continue;
              console.error("Error parsing JSON:", e);
            }
          }
        }

        // Keep any incomplete line in the buffer
        const lastNewlineIndex = buffer.lastIndexOf("\n");
        if (lastNewlineIndex !== -1) {
          buffer = buffer.slice(lastNewlineIndex + 1);
        }
      }
    } catch (error) {
      console.error("Stream error:", error);
      if (onError) {
        onError(error as Error);
      }
    }
  }

  static async submitChat(
    tripContext: TripContext,
    chatId: string,
    message: string,
    onChunk: (data: any) => void,
    onError?: (error: Error) => void
  ): Promise<void> {
    const chatBody: ChatBody = {
      chat_id: chatId,
      trip_context: tripContext,
      new_message: message,
      user_name: useAuthStore.getState().user?.first_name ?? "",
    };

    const accessToken = await this.getAccessToken();
    const response = await fetch(`${this.BASE_URL}/trip/chat/submit`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(chatBody),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    try {
      const reader = response.body?.getReader();
      const decoder = new TextDecoder("utf-8");

      if (!reader) {
        throw new Error("No reader available");
      }

      let buffer = "";
      let streamIdFound = false;
      let reading = true;

      while (reading) {
        const { done, value } = await reader.read();
        if (done) {
          reading = false;
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split("\n");

        // Process all complete lines except the last one (which might be incomplete)
        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i];
          if (line && line.startsWith("data: ")) {
            try {
              // Extract the JSON part
              const jsonStr = line.slice(6);
              const parsed = JSON.parse(jsonStr);

              // Check for stream_id in the first message
              if (
                !streamIdFound &&
                parsed.type === "stream_init" &&
                parsed.data?.stream_id
              ) {
                streamIdFound = true;
              }

              // Pass the complete chunk to the callback
              onChunk(line);
            } catch (error) {
              console.error("Error parsing JSON in submitChat:", error);
            }
          } else if (line && line.trim() !== "") {
            // Handle non-data lines if needed
            onChunk(line);
          }
        }

        // Keep the last line in the buffer (it might be incomplete)
        buffer = lines[lines.length - 1] || "";
      }

      // Process any remaining data in the buffer
      if (buffer.trim() !== "") {
        onChunk(buffer);
      }
    } catch (error) {
      console.error("Stream error:", error);
      onError?.(error as Error);
      throw error;
    }
  }

  static async streamDescription(
    trip: Trip,
    onChunk: (chunk: string) => void,
    onError?: (error: Error) => void
  ): Promise<void> {
    const descriptionBody: DescriptionBody = {
      trip: trip,
    };
    return this.streamResponse(
      "description",
      descriptionBody,
      onChunk,
      onError
    );
  }

  static async getActivityDescription(
    locationName: string,
    activityName: string,
    onChunk: (chunk: string) => void,
    onError?: (error: Error) => void
  ): Promise<void> {
    const activityDescriptionBody: ActivityDescriptionBody = {
      locationName: locationName,
      activityName: activityName,
    };
    return this.streamResponse(
      "activityDescription",
      activityDescriptionBody,
      onChunk,
      onError
    );
  }

  static async getChatHistory(
    tripId: string,
    waytoraId: string,
    locationId: string
  ): Promise<ChatHistory> {
    const params = new URLSearchParams({
      trip_id: tripId,
      location_id: locationId,
      waytora_id: waytoraId,
    });

    const accessToken = await this.getAccessToken();
    const response = await fetch(`${this.BASE_URL}/trip/chat?${params}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch chat history");
    }

    return response.json();
  }

  static async clearChat(chatId: string): Promise<void> {
    const accessToken = await this.getAccessToken();
    await fetch(`${this.BASE_URL}/trip/chat`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        chat_id: chatId,
      }),
    });
  }
}
